import { t } from "@/lib/i18n/interpolate";
import { shortExpansion } from "@/lib/tool-kit/arithmetic/division-view";
import { decimalExpansion } from "@/lib/tool-kit/arithmetic/expansion";
import {
  exponentLaws,
  type LawRow,
  log10Rational,
  type PowerResult,
  power,
  powerDecimal,
  scientificFromDigits,
  scientificFromLog10,
} from "@/lib/tool-kit/arithmetic/power";
import {
  absBig,
  BIG_ONE,
  BIG_ZERO,
  formatBig,
} from "@/lib/tool-kit/math/bigint";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  formatStyled,
  type NumberStyle,
  parseRational,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/**
 * Everything the exponent calculator shows: the exact (or approximate) power, the
 * working in words, the notes about conventions, and the exponent laws checked on the
 * reader's own numbers.
 */
export type PowerNote =
  | "zero-zero"
  | "zero-negative"
  | "complex"
  | "odd-root"
  | "huge"
  | "approximate";

export interface PowerView {
  base: Rational;
  exponent: Rational;
  style: NumberStyle;
  baseText: string;
  exponentText: string;
  result: PowerResult;
  /** Decimal digits of an irrational result, or null. */
  decimals: string | null;
  places: number;
  notes: PowerNote[];
  laws: LawRow[];
  /** The exponent laws' second exponent, when it is a whole number. */
  second: bigint | null;
}

export type PowerComputation =
  | { kind: "empty" | "invalid-base" | "invalid-exponent" }
  | { kind: "ok"; view: PowerView };

export interface PowerWords {
  negativeRule: string;
  repeated: string;
  binary: string;
  squarings: string;
  combine: string;
  squareAndMultiply: string;
  reciprocal: string;
  fractionalRule: string;
  rootSquare: string;
  rootCube: string;
  rootIndex: string;
  rootIs: string;
  rootPower: string;
  irrational: string;
  zeroRule: string;
  oneRule: string;
  hugeRule: string;
}

const MAX_SHORT = 28;
const SIG_FIGS = 10;
const MAX_BINARY_EXPONENT = BigInt(4096);
const MAX_LAW_EXPONENT = 60;

/* ----------------------------------------------------------- formatting */

function sign(negative: boolean): string {
  return negative ? "−" : "";
}

export function scientificText(value: Rational): string {
  const negative = value.num < BIG_ZERO;
  const magnitude = absBig(value.num);
  let parts = scientificFromLog10(log10Rational(value), SIG_FIGS);
  if (value.den === BIG_ONE) {
    parts = scientificFromDigits(magnitude.toString(), SIG_FIGS);
  }
  return `${sign(negative)}${parts.mantissa} × 10${toSuperscript(parts.exponent)}`;
}

/** The value in full if it is short enough, otherwise in scientific notation. */
export function formatValue(value: Rational, style: NumberStyle): string {
  const text = formatStyled(value, style);
  if (text.length <= MAX_SHORT) {
    return text;
  }
  return `≈ ${scientificText(value)}`;
}

function wrapBase(text: string): string {
  const needs =
    text.startsWith("−") || text.includes("/") || text.includes(" ");
  return needs ? `(${text})` : text;
}

function showExponent(text: string): string {
  return text.startsWith("−") || text.includes("/") ? `(${text})` : text;
}

/* --------------------------------------------------------- computation */

export function computePower(
  rawBase: string,
  rawExponent: string,
  rawSecond: string,
  places: number
): PowerComputation {
  if (rawBase.trim() === "" || rawExponent.trim() === "") {
    return { kind: "empty" };
  }
  const base = parseRational(rawBase);
  if (base === null) {
    return { kind: "invalid-base" };
  }
  const exponent = parseRational(rawExponent);
  if (exponent === null) {
    return { kind: "invalid-exponent" };
  }
  const result = power(base, exponent);
  const secondRational = parseRational(rawSecond);
  const second =
    secondRational !== null && secondRational.den === BIG_ONE
      ? secondRational.num
      : null;
  const style: NumberStyle = rawBase.includes("/") ? "fraction" : "decimal";
  const decimals =
    result.kind === "irrational" ? powerDecimal(base, exponent, places) : null;
  const view: PowerView = {
    base,
    exponent,
    style,
    baseText: formatStyled(base, style),
    exponentText: formatStyled(
      exponent,
      rawExponent.includes("/") ? "fraction" : "decimal"
    ),
    result,
    decimals,
    places,
    notes: notesFor(base, exponent, result, decimals),
    laws: [],
    second,
  };
  view.laws = lawsFor(view);
  return { kind: "ok", view };
}

function notesFor(
  base: Rational,
  exponent: Rational,
  result: PowerResult,
  decimals: string | null
): PowerNote[] {
  const notes: PowerNote[] = [];
  if (base.num === BIG_ZERO && exponent.num === BIG_ZERO) {
    notes.push("zero-zero");
  }
  if (result.kind === "undefined") {
    notes.push("zero-negative");
  }
  if (result.kind === "complex") {
    notes.push("complex");
  }
  if (
    base.num < BIG_ZERO &&
    exponent.den !== BIG_ONE &&
    result.kind !== "complex"
  ) {
    notes.push("odd-root");
  }
  if (result.kind === "huge") {
    notes.push("huge");
  }
  if (result.kind === "irrational" && decimals === null) {
    notes.push("approximate");
  }
  return notes;
}

function lawsFor(view: PowerView): LawRow[] {
  const { exponent, second, base } = view;
  if (exponent.den !== BIG_ONE || second === null) {
    return [];
  }
  const limit = BigInt(MAX_LAW_EXPONENT);
  if (absBig(exponent.num) > limit || absBig(second) > limit) {
    return [];
  }
  return exponentLaws(base, exponent.num, second);
}

/* -------------------------------------------------------------- working */

function binaryTerms(n: bigint): bigint[] {
  const bits = n.toString(2);
  const terms: bigint[] = [];
  for (let i = 0; i < bits.length; i++) {
    if (bits.at(-1 - i) === "1") {
      terms.push(BigInt(2) ** BigInt(i));
    }
  }
  return terms;
}

function powerOf(base: Rational, n: bigint): Rational | null {
  const result = power(base, { num: n, den: BIG_ONE });
  return result.kind === "exact" ? result.value : null;
}

/** Lines that build base^n for n ≥ 2 by multiplication. */
function positiveLines(
  base: Rational,
  n: bigint,
  style: NumberStyle,
  baseText: string,
  words: PowerWords
): string[] {
  const value = powerOf(base, n);
  if (value === null) {
    return [t(words.hugeRule, { base: wrapBase(baseText), n: n.toString() })];
  }
  const shown = formatValue(value, style);
  if (n <= BigInt(6)) {
    const factors = Array.from({ length: Number(n) }, () => wrapBase(baseText));
    return [t(words.repeated, { product: factors.join(" × "), value: shown })];
  }
  const terms = binaryTerms(n);
  const lines = [
    t(words.binary, {
      n: n.toString(),
      bits: n.toString(2),
      sum: terms.map(String).join(" + "),
    }),
  ];
  if (n > MAX_BINARY_EXPONENT) {
    lines.push(t(words.squareAndMultiply, { value: shown }));
    return lines;
  }
  const squarings: string[] = [];
  const top = terms.at(-1) ?? BIG_ONE;
  for (let step = BIG_ONE; step <= top; step *= BigInt(2)) {
    const stepValue = powerOf(base, step);
    squarings.push(
      `${wrapBase(baseText)}${toSuperscript(Number(step))} = ${stepValue ? formatValue(stepValue, style) : "…"}`
    );
  }
  lines.push(t(words.squarings, { list: squarings.join(", ") }));
  lines.push(
    t(words.combine, {
      parts: terms
        .map((term) => `${wrapBase(baseText)}${toSuperscript(Number(term))}`)
        .join(" × "),
      value: shown,
    })
  );
  return lines;
}

function integerLines(view: PowerView, words: PowerWords): string[] {
  const { base, exponent, style, baseText } = view;
  const n = exponent.num;
  const b = wrapBase(baseText);
  if (n === BIG_ZERO) {
    return [t(words.zeroRule, { base: b })];
  }
  if (absBig(n) === BIG_ONE && n > BIG_ZERO) {
    return [t(words.oneRule, { base: b })];
  }
  if (n > BIG_ZERO) {
    return positiveLines(base, n, style, baseText, words);
  }
  const m = absBig(n);
  const lines = [t(words.negativeRule, { base: b, n: m.toString() })];
  if (m > BIG_ONE) {
    lines.push(...positiveLines(base, m, style, baseText, words));
  }
  const positive = powerOf(base, m);
  if (positive !== null && view.result.kind === "exact") {
    lines.push(
      t(words.reciprocal, {
        value: formatValue(positive, style),
        result: formatValue(view.result.value, style),
      })
    );
  }
  return lines;
}

function rootName(q: bigint, words: PowerWords): string {
  if (q === BigInt(2)) {
    return words.rootSquare;
  }
  if (q === BigInt(3)) {
    return words.rootCube;
  }
  return t(words.rootIndex, { q: q.toString() });
}

function fractionalLines(view: PowerView, words: PowerWords): string[] {
  const { exponent, baseText, result, style, base } = view;
  const q = exponent.den;
  const root = rootName(q, words);
  const lines = [
    t(words.fractionalRule, {
      base: wrapBase(baseText),
      exponent: showExponent(view.exponentText),
      root,
      p: formatBig(exponent.num),
    }),
  ];
  if (result.kind === "irrational") {
    lines.push(t(words.irrational, { root, base: wrapBase(baseText) }));
    return lines;
  }
  const rooted = power(
    { num: absBig(base.num), den: base.den },
    { num: BIG_ONE, den: q }
  );
  if (result.kind === "exact" && rooted.kind === "exact") {
    const rootValue =
      base.num < BIG_ZERO
        ? { num: -rooted.value.num, den: rooted.value.den }
        : rooted.value;
    const shown = formatStyled(rootValue, style);
    lines.push(
      t(words.rootIs, { root, base: wrapBase(baseText), value: shown }),
      t(words.rootPower, {
        value: wrapBase(shown),
        p: formatBig(exponent.num),
        result: formatValue(result.value, style),
      })
    );
  }
  return lines;
}

/** The working, one sentence per line. */
export function powerWorking(view: PowerView, words: PowerWords): string[] {
  if (view.result.kind === "undefined" || view.result.kind === "complex") {
    return [];
  }
  return view.exponent.den === BIG_ONE
    ? integerLines(view, words)
    : fractionalLines(view, words);
}

/* ------------------------------------------------------------- summary */

export interface PowerSummary {
  /** The headline value as text. */
  headline: string;
  /** A decimal reading for fractions, if different from the headline. */
  decimal: string | null;
  /** Scientific notation for very large or small values. */
  scientific: string | null;
  /** The whole digits of an exact integer, when there are many. */
  fullDigits: string | null;
  /** Digit count of an integer result. */
  digits: number | null;
}

const SCIENTIFIC_ABOVE = 1_000_000;

function summarizeExact(value: Rational, view: PowerView): PowerSummary {
  const integer = value.den === BIG_ONE;
  const headline = formatValue(value, view.style);
  const magnitudeLog = value.num === BIG_ZERO ? 0 : log10Rational(value);
  const decimal =
    integer || (view.style === "decimal" && headline.indexOf("/") === -1)
      ? null
      : shortExpansion(decimalExpansion(value.num, value.den));
  const wantsScientific =
    value.num !== BIG_ZERO &&
    (magnitudeLog >= Math.log10(SCIENTIFIC_ABOVE) || magnitudeLog < -4);
  const digitsText = integer ? absBig(value.num).toString() : null;
  return {
    headline,
    decimal,
    scientific: wantsScientific ? scientificText(value) : null,
    fullDigits:
      digitsText !== null && digitsText.length > MAX_SHORT
        ? formatBig(value.num)
        : null,
    digits: digitsText === null ? null : digitsText.length,
  };
}

export function summarizePower(view: PowerView): PowerSummary | null {
  const { result } = view;
  if (result.kind === "exact") {
    return summarizeExact(result.value, view);
  }
  if (result.kind === "irrational") {
    const text =
      view.decimals === null ? "" : `${sign(result.negative)}${view.decimals}`;
    return {
      headline: view.decimals === null ? "≈" : `≈ ${text}`,
      decimal: null,
      scientific: null,
      fullDigits: null,
      digits: null,
    };
  }
  if (result.kind === "huge") {
    const sci = scientificFromLog10(result.log10, SIG_FIGS);
    return {
      headline: `≈ ${sign(result.negative)}${sci.mantissa} × 10${toSuperscript(sci.exponent)}`,
      decimal: null,
      scientific: null,
      fullDigits: null,
      digits: sci.exponent >= 0 ? sci.exponent + 1 : null,
    };
  }
  return null;
}

/* ------------------------------------------------------------------ laws */

function sup(n: bigint): string {
  return toSuperscript(Number(n));
}

/** One row of the exponent-law table as language-neutral maths. */
function lawSides(
  view: PowerView,
  row: LawRow
): { left: string; right: string } {
  const b = wrapBase(view.baseText);
  const v = (value: Rational) => formatValue(value, view.style);
  const w = (value: Rational) => wrapBase(v(value));
  const m = row.exponents[0];
  const n = row.exponents.at(-1) ?? m;
  switch (row.id) {
    case "product":
      return {
        left: `${b}${sup(m)} × ${b}${sup(n)} = ${w(row.leftParts[0])} × ${w(row.leftParts[1])} = ${v(row.left)}`,
        right: `${b}${sup(m + n)} = ${v(row.right)}`,
      };
    case "quotient":
      return {
        left: `${b}${sup(m)} ÷ ${b}${sup(n)} = ${w(row.leftParts[0])} ÷ ${w(row.leftParts[1])} = ${v(row.left)}`,
        right: `${b}${sup(m - n)} = ${v(row.right)}`,
      };
    case "power":
      return {
        left: `(${b}${sup(m)})${sup(n)} = ${w(row.leftParts[0])}${sup(n)} = ${v(row.left)}`,
        right: `${b}${sup(m * n)} = ${v(row.right)}`,
      };
    case "negative":
      return {
        left: `${b}${sup(-n)} = ${v(row.left)}`,
        right: `1 / ${b}${sup(n)} = 1 / ${w(row.leftParts[0])} = ${v(row.right)}`,
      };
    default:
      return { left: `${b}⁰ = 1`, right: "" };
  }
}

/** The law row as one line of maths: both sides, evaluated. */
export function lawExpression(view: PowerView, row: LawRow): string {
  const sides = lawSides(view, row);
  return sides.right === "" ? sides.left : `${sides.left}; ${sides.right}`;
}
